import { Settings } from '@atlaskit/atlassian-navigation';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { HeadingItem, MenuGroup, Section } from '@atlaskit/menu';
import Popup, { ContentProps } from '@atlaskit/popup';
import { isFedRamp } from '@atlassian/atl-context';
import React, { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { LINK_URLS } from '../../../../constants/links';
import TrackedLinkItem from '../../../common/TrackedLinkItem';

const SettingsContent: React.FC<ContentProps> = () => (
  <MenuGroup minWidth="300px">
    <Section>
      <HeadingItem>
        <FormattedMessage {...messages.settings} />
      </HeadingItem>
      <TrackedLinkItem
        description={<FormattedMessage {...messages.adminDescription} />}
        iconAfter={<ShortcutIcon label="" />}
        href={LINK_URLS.ADMIN_ATLASSIAN}
        actionSubject="menuItem"
        action="clicked"
        extraAnalyticProps={{
          item: 'cloudAdmin',
        }}
        openInNewTab
        synchroniseSession={true}
        synchronisationApplication="admin"
      >
        <FormattedMessage {...messages.admin} />
      </TrackedLinkItem>
      {!isFedRamp() && (
        <TrackedLinkItem
          description={<FormattedMessage {...messages.licensingLinkDescription} />}
          iconAfter={<ShortcutIcon label="" />}
          href={LINK_URLS.LICENSING}
          actionSubject="menuItem"
          action="clicked"
          extraAnalyticProps={{
            item: 'licensing',
          }}
          openInNewTab
          synchroniseSession={true}
          synchronisationApplication="mac"
        >
          <FormattedMessage {...messages.licensingLink} />
        </TrackedLinkItem>
      )}
    </Section>
  </MenuGroup>
);

const NavSettingsButton: React.FC = () => {
  const [isOpen, setOpen] = useState(false);
  return (
    <Popup
      placement="bottom-end"
      content={SettingsContent}
      isOpen={isOpen}
      testId="nav__settings-menu"
      onClose={() => setOpen(false)}
      trigger={triggerProps => (
        <Settings
          isSelected={isOpen}
          testId="nav__settings-menu-trigger"
          onClick={() => setOpen(!isOpen)}
          tooltip={<FormattedMessage {...messages.settings} />}
          {...triggerProps}
        />
      )}
    />
  );
};

export default NavSettingsButton;

const messages = defineMessages({
  admin: {
    defaultMessage: 'Cloud Administration',
    description: 'Link to the administration portal or site admin',
    id: 'uchi.settings.admin',
  },
  adminDescription: {
    defaultMessage: 'Manage users, security and billing',
    description: 'Description for the admin link',
    id: 'uchi.settings.adminDescription',
  },
  licensingLink: {
    defaultMessage: 'Licensing',
    description: 'Link to licensing',
    id: 'uchi.settings.licensingLink',
  },
  licensingLinkDescription: {
    defaultMessage: 'Server and Data Center licensing',
    description: 'Description for the link to licensing',
    id: 'uchi.settings.licensingLinkDescription',
  },
  settings: {
    defaultMessage: 'Settings',
    description: 'Generic label for the feature',
    id: 'uchi.settings.label',
  },
});
